import { Alert, Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { GapQuestionThread } from '../shared/GapQuestionThread';
import { type GapDomain, type GapQuestion } from '../../utils/patientGapAnalysisStore';

interface GapResponseSectionProps {
  title: string;
  items: Array<{ analysisId: string; question: GapQuestion }>;
  emptyText: string;
  domainColors: Record<GapDomain, 'error' | 'warning' | 'info' | 'success' | 'default'>;
  onResolutionChange: (analysisId: string, questionId: string, status: 'OPEN' | 'RESOLVED') => void;
}

export const GapResponseSection = ({
  title,
  items,
  emptyText,
  domainColors,
  onResolutionChange,
}: GapResponseSectionProps) => (
  <Box>
    <Typography variant="h6" gutterBottom>{title}</Typography>
    {items.length === 0 ? (
      <Alert severity="info">{emptyText}</Alert>
    ) : (
      <Stack spacing={2}>
        {items.map(({ analysisId, question }) => (
          <Paper key={`${analysisId}-${question.id}`} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
              <Chip label={question.gapLabel} size="small" color={domainColors[question.domain]} />
              <Chip
                label={question.severity}
                size="small"
                color={question.severity === 'HIGH' ? 'error' : question.severity === 'MEDIUM' ? 'warning' : 'default'}
              />
              <Chip
                label={question.resolutionStatus === 'RESOLVED' ? 'Resolved' : 'Still Open'}
                size="small"
                color={question.resolutionStatus === 'RESOLVED' ? 'success' : 'warning'}
              />
              {question.lastUpdatedAt && (
                <Typography variant="caption" color="text.secondary">
                  Last updated {new Date(question.lastUpdatedAt).toLocaleString()}
                </Typography>
              )}
            </Stack>
            <Typography variant="subtitle2" gutterBottom>{question.question}</Typography>
            <GapQuestionThread
              thread={question.thread}
              emptyText="No patient or clinician follow-up recorded yet."
            />
            {question.patientStatus === 'ANSWERED' && (
              <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
                <Button
                  variant={question.resolutionStatus === 'OPEN' ? 'contained' : 'outlined'}
                  color="warning"
                  onClick={() => onResolutionChange(analysisId, question.id, 'OPEN')}
                >
                  Still Open
                </Button>
                <Button
                  variant={question.resolutionStatus === 'RESOLVED' ? 'contained' : 'outlined'}
                  color="success"
                  onClick={() => onResolutionChange(analysisId, question.id, 'RESOLVED')}
                >
                  Mark Resolved
                </Button>
              </Stack>
            )}
          </Paper>
        ))}
      </Stack>
    )}
  </Box>
);